import { Location } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { AutocompleteFieldComponent } from 'src/app/components/form/autocomplete-field/autocomplete-field.component';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { AlertInfo } from 'src/app/models/alert-info';
import { SavedDataInfo } from 'src/app/models/form/saved-data-info';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CompaniesService } from 'src/app/services/api/general-settings/companies.service';
import { CountriesService } from 'src/app/services/api/general-settings/countries.service';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { FormService } from 'src/app/services/form.service';
import { ValidatorService } from 'src/app/services/validator.service';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { LoaderConfig } from 'src/app/components/widgets/loader/loader.config';


@Component({
  selector: 'company-form',
  imports: [CommonModule, NgbAlert, InputFieldComponent, ReactiveFormsModule, AutocompleteFieldComponent, FormsModule, LoaderComponent],
  templateUrl: './company-form.component.html',
  styleUrl: './company-form.component.scss'
})
export class CompanyFormComponent implements OnDestroy {
  @Input() company?: any;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();
  @ViewChild('logoInput') logoInputRef!: ElementRef<HTMLInputElement>;

  form!: FormGroup;
  readonlyCompaniesForm: boolean = false;
  selectedCountryId: number | null = null;
  preventDropdownAutoOpen: boolean = false;

  logoFile: File | null = null;
  logoPreviewUrl: string | null = null;
  logoError: string | null = null;
  existingLogoUrl: string | null = null;

  logoScenario: 'transparent' | 'dark' | 'light' = 'transparent';

  isLoading: boolean = false;
  loaderConfig: LoaderConfig = { type: 'spinner', overlay: true, size: 'md' };

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  constructor(
    private validatorService: ValidatorService,
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private companiesService: CompaniesService,
    private confirmationDialogService: ConfirmationDialogService,
    private location: Location,
    private ngZone: NgZone,
    protected countriesService: CountriesService
  ) { }


  ngOnInit(): void {
    this.form = new FormGroup({
      code: new FormControl('', Validators.required),
      name: new FormControl('', Validators.required),
      countryId: new FormControl(null, Validators.required),
      companyId: new FormControl(''),
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['company'] && changes['company'].currentValue) {
      this.form.patchValue({
        code: this.company.code,
        name: this.company.name,
        countryId: this.company.country.countryId,
        companyId: this.company.companyId,
      });

      console.log('[CompanyForm] company.logo:', JSON.stringify(this.company.logo));
      const logoUrl = this.company.logo?.url ?? null;
      this.existingLogoUrl = logoUrl;

      this.revokeBlobUrl();
      if (logoUrl) {
        this.isLoading = true;
        this.companiesService.getLogo(logoUrl).subscribe({
          next: (publicUrl) => {
            console.log('[CompanyForm] publicUrl received:', publicUrl);
            this.ngZone.run(() => {
              this.logoPreviewUrl = publicUrl || null;
              this.isLoading = false;
            });
          },
          error: (err) => {
            console.error('[CompanyForm] getLogo error:', err);
            this.ngZone.run(() => {
              this.logoPreviewUrl = null;
              this.isLoading = false;
            });
          }
        });
      } else {
        this.logoPreviewUrl = null;
      }
    }
  }

  ngOnDestroy(): void {
    this.revokeBlobUrl();
  }

  private revokeBlobUrl(): void {
    if (typeof this.logoPreviewUrl === 'string' && this.logoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.logoPreviewUrl);
    }
  }

  onCountryChange(newValue: any): void {
    this.selectedCountryId = newValue;
    //this.form.patchValue({ countryId: newValue });
    console.log('Country changed:', newValue);
    console.log('Company after country change:', this.selectedCountryId);


  }

  /**
   * *******************
   * Submit form methods
   * *******************
   */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }
  submitForm(): void {
    this.preventDropdownAutoOpen = true;
    
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    if (this.form.valid) {
      const { companyId: _id, countryId: _cid, ...rest } = this.form.value;

      const values: any = {
        ...rest,
        countryId: this.selectedCountryId ?? _cid,
        tenantId: 1,
      };

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const companyId = this.company?.companyId;
      const serviceMethod = companyId
        ? this.companiesService.update(companyId, values)
        : this.companiesService.create(values);

      this.isLoading = true;
      serviceMethod.pipe(
        switchMap((response: any) => {
          const savedId = companyId ?? response.data?.company?.companyId;
          return this.handleLogoOperations(savedId).pipe(map(() => response));
        })
      ).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.submitFormSuccess(response);
        },
        error: (error: any) => {
          this.isLoading = false;
          this.submitFormError(error);
        }
      });
    }
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.logoError = null;

    if (!input.files || input.files.length === 0) {
      this.logoFile = null;
      this.logoPreviewUrl = null;
      return;
    }

    const file = input.files[0];
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    const maxSizeBytes = 2 * 1024 * 1024; // 2 MB

    if (!allowedTypes.includes(file.type)) {
      this.logoError = 'Invalid format. Accepted: PNG, JPG, SVG, WEBP.';
      this.logoFile = null;
      this.logoPreviewUrl = null;
      input.value = '';
      return;
    }

    if (file.size > maxSizeBytes) {
      this.logoError = 'File exceeds the maximum size of 2 MB.';
      this.logoFile = null;
      this.logoPreviewUrl = null;
      input.value = '';
      return;
    }

    this.logoFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.logoPreviewUrl = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  confirmRemoveLogo(): void {
    this.confirmationDialogService.open(
      'Are you sure you want to delete the company logo?',
      () => this.deleteLogo()
    );
  }

  private deleteLogo(): void {
    if (this.company?.companyId && this.existingLogoUrl) {
      this.isLoading = true;
      this.companiesService.deleteLogo(this.company.companyId).subscribe({
        next: () => {
          this.isLoading = false;
          this.alertNotificationService.success('Logo removed successfully');
          this.clearLogoState();
        },
        error: () => {
          this.isLoading = false;
          this.showAlertInfo('danger', 'Failed to delete the logo. Please try again.');
        }
      });
    } else {
      this.clearLogoState();
    }
  }

  private clearLogoState(): void {
    this.revokeBlobUrl();
    this.logoFile = null;
    this.logoPreviewUrl = null;
    this.logoError = null;
    this.existingLogoUrl = null;
    if (this.logoInputRef?.nativeElement) {
      this.logoInputRef.nativeElement.value = '';
    }
  }

  private handleLogoOperations(companyId: number): Observable<any> {
    if (this.logoFile) {
      const formData = new FormData();
      formData.append('fileLogo', this.logoFile);
      return this.companiesService.createLogo(companyId, formData);
    }
    return of(null);
  }

  goBack(): void {
    this.location.back();

  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */



  getCategoryCodeInput(): FormControl { return this.getFormControl('code'); }
  getCategoryNameInput(): FormControl { return this.getFormControl('name'); }
  getAutocompleteCountryInput(): FormControl { return this.getFormControl('countryId'); }
  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }
}
